import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, ExternalLink, FileText, Pencil, Sparkles, Trash2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { API_RUEDA } from './RuedaSyncPanel';
import { RuedaCompararDialog } from './RuedaCompararDialog';

const CORTOS = [['nombre', 'Nombre'], ['descripcion', 'Descripción breve / tipo'], ['beneficio', 'Beneficio'], ['ubicacion', 'Ubicación'],
  ['info_contacto', 'Contacto'], ['telefono', 'Teléfono'], ['whatsapp', 'WhatsApp'], ['sitio_web', 'Sitio web']];
const LARGOS = [['descripcion_larga', 'Descripción larga'], ['detalles_beneficio', 'Servicios'], ['condiciones', 'Condiciones'],
  ['horarios', 'Horarios'], ['precios_tarifas', 'Precios y tarifas'], ['descripcion_parrafo2', 'Otros datos']];

const EditarDialog = ({ item, categorias, onClose, onSaved }) => {
  const [c, setC] = useState(item.campos);
  const set = (k, v) => setC({ ...c, [k]: v });
  const guardar = async () => {
    try { await axios.put(`${API_RUEDA}/pendientes/${item.id}`, { campos: c }); toast.success('Guardado'); onSaved(); }
    catch { toast.error('No se pudo guardar'); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="rueda-editar-dialog">
        <DialogHeader><DialogTitle>Editar importación</DialogTitle></DialogHeader>
        <div>
          <Label>Categoría</Label>
          <select className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm" value={c.categoria_id || ''}
            onChange={(e) => set('categoria_id', e.target.value)} data-testid="rueda-editar-categoria">
            <option value="">Sin categorizar</option>
            {categorias.map((x) => <option key={x.id} value={x.id}>{x.nombre}</option>)}
          </select>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          {CORTOS.map(([k, l]) => <div key={k}><Label>{l}</Label><Input value={c[k] || ''} onChange={(e) => set(k, e.target.value)} data-testid={`rueda-editar-${k}`} /></div>)}
        </div>
        {LARGOS.map(([k, l]) => <div key={k}><Label>{l}</Label><Textarea rows={k === 'descripcion_larga' ? 5 : 3} value={c[k] || ''} onChange={(e) => set(k, e.target.value)} data-testid={`rueda-editar-${k}`} /></div>)}
        <div className="flex justify-end gap-2"><Button variant="outline" onClick={onClose}>Cancelar</Button><Button onClick={guardar} data-testid="rueda-editar-guardar">Guardar</Button></div>
      </DialogContent>
    </Dialog>
  );
};

const Tarjeta = ({ it, catNombre, onEditar, onPublicar, onDescartar, seleccionado, onSeleccionar }) => {
  const c = it.campos;
  return (
    <div className={`rounded-xl border bg-white p-4 space-y-2 ${seleccionado ? 'ring-2 ring-primary' : ''}`} data-testid={`rueda-pendiente-${it.id}`}>
      <div className="flex flex-wrap items-start gap-3">
        <input type="checkbox" className="mt-1 h-4 w-4 accent-primary disabled:opacity-30" checked={seleccionado} disabled={!c.categoria_id}
          title={c.categoria_id ? 'Seleccionar para publicar en lote' : 'Asigná una categoría primero'}
          onChange={(e) => onSeleccionar(e.target.checked)} data-testid={`rueda-seleccionar-${it.id}`} />
        {c.imagen_principal && <img src={c.imagen_principal} alt="" className="h-14 w-20 sm:h-16 sm:w-24 rounded object-cover" />}
        <div className="flex-1 min-w-[160px]">
          <p className="font-semibold">{c.nombre}</p>
          <p className="text-xs text-gray-500">{it.categoria_rueda} · {it.tipo_rueda}</p>
          <div className="mt-1 flex flex-wrap gap-1 text-xs">
            {catNombre ? <span className="rounded-full bg-green-100 px-2 text-green-700">{catNombre}</span>
              : <span className="rounded-full bg-amber-100 px-2 text-amber-700" data-testid={`rueda-sin-cat-${it.id}`}>Sin categorizar</span>}
            {it.es_actualizacion && <span className="rounded-full bg-blue-100 px-2 text-blue-700">Cambios de uno ya publicado</span>}
            {it.ia?.completados?.length > 0 && <span className="flex items-center gap-1 rounded-full bg-violet-100 px-2 text-violet-700"><Sparkles className="h-3 w-3" />IA completó {it.ia.completados.length} campos</span>}
          </div>
        </div>
        <div className="flex w-full sm:w-auto justify-end gap-1">
          <Button size="sm" variant="ghost" onClick={onEditar} data-testid={`rueda-editar-${it.id}`}><Pencil className="h-4 w-4" /></Button>
          <Button size="sm" variant="ghost" onClick={onDescartar} data-testid={`rueda-descartar-${it.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
          <Button size="sm" onClick={onPublicar} disabled={!c.categoria_id} data-testid={`rueda-publicar-${it.id}`}><Check className="h-4 w-4 mr-1" />Publicar</Button>
        </div>
      </div>
      <p className="text-sm text-gray-700"><b>Beneficio:</b> {c.beneficio || '—'} {c.condiciones && <span className="text-gray-500">· {c.condiciones.slice(0, 160)}</span>}</p>
      {c.descripcion_larga && <p className="text-xs text-gray-600 line-clamp-3">{c.descripcion_larga}</p>}
      <div className="flex flex-wrap gap-3 text-xs text-gray-500">
        {c.ubicacion && <span>{c.ubicacion}</span>}{c.telefono && <span>Tel {c.telefono}</span>}
        {(it.media?.pdfs || []).map((p, i) => <a key={i} href={p.url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-primary"><FileText className="h-3 w-3" />PDF {i + 1}</a>)}
        {it.url_rueda && <a href={it.url_rueda} target="_blank" rel="noreferrer" className="flex items-center gap-1"><ExternalLink className="h-3 w-3" />Ver en la Rueda</a>}
      </div>
    </div>
  );
};

export const RuedaPendientesPanel = ({ categorias, recargarToken, onPublicado }) => {
  const [data, setData] = useState({ items: [], conteo: {} });
  const [editando, setEditando] = useState(null);
  const [comparar, setComparar] = useState(null);
  const [filtro, setFiltro] = useState('todos');
  const [busy, setBusy] = useState(false);
  const [sel, setSel] = useState([]);
  const [resultado, setResultado] = useState(null);

  const publicarLote = async () => {
    if (!window.confirm(`¿Publicar ${sel.length} prestadores en la Guía?`)) return;
    setBusy(true);
    try {
      const r = (await axios.post(`${API_RUEDA}/pendientes/publicar-lote`, { ids: sel }, { timeout: 120000 })).data;
      setResultado(r);
      setSel(r.salteados.filter((s) => s.duplicado).map((s) => s.id));
      if (r.publicados.length) toast.success(`${r.publicados.length} publicados en la Guía`);
      cargar(); if (r.publicados.length) onPublicado?.();
    } catch { toast.error('No se pudo publicar el lote'); } finally { setBusy(false); }
  };
  const cargar = useCallback(() => axios.get(`${API_RUEDA}/pendientes`).then((r) => setData(r.data)), []);
  useEffect(() => { cargar(); }, [cargar, recargarToken]);

  const publicar = async (it, modo, prestadorId) => {
    setBusy(true);
    try {
      await axios.post(`${API_RUEDA}/pendientes/${it.id}/publicar`, { modo: modo || null, prestador_id: prestadorId || null });
      toast.success('Prestador publicado en la Guía');
      setComparar(null); cargar(); onPublicado?.();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.code === 'duplicado') setComparar({ it, ...d });
      else toast.error(typeof d === 'string' ? d : 'No se pudo publicar');
    } finally { setBusy(false); }
  };
  const descartar = async (it) => {
    if (!window.confirm(`¿Descartar "${it.campos.nombre}"? Volverá a aparecer solo si cambia en la Rueda.`)) return;
    await axios.post(`${API_RUEDA}/pendientes/${it.id}/descartar`); cargar();
  };

  const catNombre = (id) => categorias.find((c) => c.id === id)?.nombre;
  const items = data.items.filter((i) => filtro === 'todos' || i.estado === filtro);
  const seleccionables = items.filter((i) => i.campos.categoria_id).map((i) => i.id);
  const todosSel = seleccionables.length > 0 && seleccionables.every((id) => sel.includes(id));
  const toggle = (id, v) => setSel(v ? [...new Set([...sel, id])] : sel.filter((x) => x !== id));
  return (
    <div className="space-y-3" data-testid="rueda-pendientes-panel">
      <div className="flex flex-wrap gap-2 text-sm">
        {[['todos', 'Todos'], ['borrador', `Borradores (${data.conteo.borrador || 0})`], ['sin_categorizar', `Sin categorizar (${data.conteo.sin_categorizar || 0})`]].map(([k, l]) => (
          <button key={k} onClick={() => setFiltro(k)} data-testid={`rueda-filtro-${k}`}
            className={`rounded-full px-3 py-1 ${filtro === k ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700'}`}>{l}</button>
        ))}
        <span className="ml-auto text-xs text-gray-500 self-center">Publicados: {data.conteo.publicado || 0} · Descartados: {data.conteo.descartado || 0}</span>
      </div>
      {seleccionables.length > 0 && (
        <div className="sticky top-0 z-10 flex flex-wrap items-center gap-3 rounded-lg border bg-white/95 backdrop-blur px-3 py-2" data-testid="rueda-lote-barra">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" className="h-4 w-4 accent-primary" checked={todosSel}
              onChange={(e) => setSel(e.target.checked ? [...new Set([...sel, ...seleccionables])] : sel.filter((x) => !seleccionables.includes(x)))}
              data-testid="rueda-seleccionar-todos" />
            Seleccionar todos los que tienen categoría ({seleccionables.length})
          </label>
          <Button size="sm" className="ml-auto" disabled={!sel.length || busy} onClick={publicarLote} data-testid="rueda-publicar-lote-btn">
            <Check className="h-4 w-4 mr-1" />Publicar seleccionados ({sel.length})
          </Button>
        </div>
      )}
      {resultado && (
        <div className="rounded-lg border bg-gray-50 p-3 text-sm space-y-1" data-testid="rueda-lote-resultado">
          <div className="flex justify-between"><b>{resultado.publicados.length} publicados · {resultado.salteados.length} salteados</b>
            <button className="text-xs text-gray-500" onClick={() => setResultado(null)}>Cerrar</button></div>
          {resultado.salteados.map((s) => <p key={s.id} className="text-xs text-amber-800">• {s.nombre}: {s.motivo}</p>)}
          {resultado.salteados.some((s) => s.duplicado) && <p className="text-xs text-gray-500">Los duplicados quedaron seleccionados: tocá "Publicar" en cada uno para compararlos.</p>}
        </div>
      )}
      {items.map((it) => (
        <Tarjeta key={it.id} it={it} catNombre={catNombre(it.campos.categoria_id)} onEditar={() => setEditando(it)}
          seleccionado={sel.includes(it.id)} onSeleccionar={(v) => toggle(it.id, v)}
          onPublicar={() => publicar(it)} onDescartar={() => descartar(it)} />
      ))}
      {!items.length && <p className="py-8 text-center text-sm text-gray-400" data-testid="rueda-pendientes-vacio">No hay importaciones pendientes.</p>}
      {editando && <EditarDialog item={editando} categorias={categorias} onClose={() => setEditando(null)} onSaved={() => { setEditando(null); cargar(); }} />}
      {comparar && <RuedaCompararDialog existente={comparar.existente} propuesto={comparar.propuesto} busy={busy}
        onCancel={() => setComparar(null)} onDecidir={(modo, pid) => publicar(comparar.it, modo, pid)} />}
    </div>
  );
};
